import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, ClipboardList, Folder, Link2, Calendar, CornerDownRight, ArrowLeft, Rocket } from 'lucide-react';
import { apiClient } from '../packages/api-client';
import { createProject, deleteProject, updateProject } from '../packages/api-client/pm-api';
import { getAIStatus, type AIUnavailableReason } from '../packages/api-client/ai-credentials-api';
import { ProjectTypeField } from './ProjectTypeField';
import type { ProjectType } from '../packages/api-client/pm-api';
import { Dialog, StateView } from '../packages/ui';

interface PlannedTask {
  title: string;
  description?: string;
  phase?: string;
  priority?: string;
  due_date?: string;
  subtasks?: PlannedTask[];
}
interface PlanResponse {
  project_name: string;
  summary: string;
  tasks: PlannedTask[];
  created?: boolean;
}
interface DraftProject {
  id: string;
  name: string;
}

interface IdeaToProjectProps {
  onClose: () => void;
  onProjectCreated?: (projectId: string) => void;
}

const UNAVAILABLE: Record<Exclude<AIUnavailableReason, ''>, { title: string; body: string }> = {
  not_configured: {
    title: 'AI is not configured',
    body: 'This workspace has no AI key set up, so a plan cannot be generated. A workspace admin can add an Anthropic API key in AI settings. You can still create a project manually.',
  },
  disabled: {
    title: 'AI is turned off',
    body: 'AI features are currently disabled on this server. You can still create a project manually.',
  },
  quota_exceeded: {
    title: 'Monthly AI limit reached',
    body: 'This workspace has used its free monthly AI allowance. A workspace admin can add an Anthropic API key in AI settings to keep using AI. You can still create a project manually.',
  },
};

type Availability = { state: 'checking' } | { state: 'available' } | { state: 'unavailable'; reason: Exclude<AIUnavailableReason, ''> } | { state: 'error' };

function apiError(e: unknown, fallback: string): string {
  const data = (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
  return data?.error || data?.message || fallback;
}

const IDEAS = [
  'Launch a mobile app in 3 months',
  'Run a product marketing campaign',
  'Build a website redesign',
  'Plan a company event',
];

/**
 * Idea → Project. The co-pilot's generate-plan route is project-scoped, so a
 * draft project is created before the first preview. The draft is reused
 * across retries and deleted again if generation fails or the dialog is
 * closed without creating tasks.
 */
export function IdeaToProject({ onClose, onProjectCreated }: IdeaToProjectProps) {
  const navigate = useNavigate();
  const [availability, setAvailability] = useState<Availability>({ state: 'checking' });
  const [prompt, setPrompt] = useState('');
  const [projectType, setProjectType] = useState<ProjectType>('general');
  const [plan, setPlan] = useState<PlanResponse | null>(null);
  const [draft, setDraft] = useState<DraftProject | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [createdId, setCreatedId] = useState('');
  // committed: task creation has started, so the project is real and must
  // never be deleted. closed: the dialog is gone; a draft that resolves
  // afterwards is removed.
  const committed = useRef(false);
  const closed = useRef(false);

  const checkAvailability = useCallback(() => {
    setAvailability({ state: 'checking' });
    getAIStatus()
      .then(s => setAvailability(s.available ? { state: 'available' } : s.reason ? { state: 'unavailable', reason: s.reason } : { state: 'error' }))
      .catch(() => setAvailability({ state: 'error' }));
  }, []);
  useEffect(checkAvailability, [checkAvailability]);

  // Removes the draft project. Returns an error message when it could not be removed.
  async function discardDraft(d: DraftProject): Promise<string> {
    try {
      await deleteProject(d.id);
      setDraft(null);
      return '';
    } catch {
      return `The draft project "${d.name}" could not be removed; delete it from Projects.`;
    }
  }

  async function close() {
    closed.current = true;
    if (draft && !committed.current) await discardDraft(draft);
    onClose();
  }

  const generate = async () => {
    if (!prompt.trim()) return;
    setLoading(true); setError(''); setPlan(null);
    let current = draft;
    try {
      if (!current) {
        const p = await createProject({ name: prompt.slice(0, 60), description: 'AI-generated project', project_type: projectType, icon: 'lightbulb', color: '#14867A' });
        current = { id: p.id, name: p.name };
        if (closed.current) { await deleteProject(p.id).catch(() => {}); return; }
        setDraft(current);
      } else {
        await updateProject(current.id, { project_type: projectType });
      }
      const { data } = await apiClient.post<PlanResponse>(`/projects/${current.id}/ai/generate-plan`, { prompt, create: false });
      if (data.project_name && data.project_name !== current.name) {
        try {
          await updateProject(current.id, { name: data.project_name });
          current = { ...current, name: data.project_name };
          setDraft(current);
        } catch { /* the draft keeps its prompt-derived name; the preview still shows the suggestion */ }
      }
      setPlan(data);
    } catch (e) {
      if (closed.current) { if (current) await deleteProject(current.id).catch(() => {}); return; }
      const msg = apiError(e, 'Failed to generate a project plan.');
      const leftover = current ? await discardDraft(current) : '';
      setError(leftover ? `${msg} ${leftover}` : msg);
    } finally { setLoading(false); }
  };

  const createAll = async () => {
    if (!draft || !prompt.trim()) return;
    committed.current = true;
    setCreating(true); setError('');
    try {
      await apiClient.post(`/projects/${draft.id}/ai/generate-plan`, { prompt, create: true });
      onProjectCreated?.(draft.id);
      navigate(`/projects/${draft.id}`);
      onClose();
    } catch (e) {
      // A failure or timeout here may still have added some tasks server-side.
      setError(`Project created; some tasks may not have been added. Open it to check. (${apiError(e, 'Task creation failed.')})`);
      setCreatedId(draft.id);
      setCreating(false);
    }
  };

  let body: React.ReactNode;
  if (availability.state === 'checking') {
    body = <StateView state="loading" label="Checking AI availability" lines={2} />;
  } else if (availability.state === 'error') {
    body = <StateView state="error" title="Couldn't check AI availability" message="Nothing was created." onRetry={checkAvailability} />;
  } else if (availability.state === 'unavailable') {
    const copy = UNAVAILABLE[availability.reason];
    body = (
      <div className="ui-state ui-state--denied" data-testid="ai-unavailable">
        <p className="ui-state-title">{copy.title}</p>
        <p className="ui-state-body">{copy.body}</p>
        {availability.reason !== 'disabled' && (
          <div className="ui-state-actions"><Link to="/admin/ai" className="btn btn-secondary btn-md">AI settings</Link></div>
        )}
      </div>
    );
  } else if (!plan || !draft) {
    body = (
      <div className="idea-input-section">
        <div className="idea-chips" aria-label="Example ideas" role="group">
          {IDEAS.map(idea => (
            <button key={idea} type="button" className="idea-chip" onClick={() => setPrompt(idea)}>{idea}</button>
          ))}
        </div>
        <ProjectTypeField id="idea-project-type" value={projectType} onChange={setProjectType} disabled={loading} />
        <label className="form-label" htmlFor="idea-prompt">Describe the project</label>
        <textarea
          id="idea-prompt"
          className="ai-textarea idea-textarea"
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          rows={5}
          autoFocus
        />
        {error && <div className="ai-error" role="alert">{error}</div>}
        <div className="idea-input-actions">
          <button type="button" className="btn btn-primary btn-md" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}
            onClick={generate} disabled={loading || !prompt.trim()}>
            <Sparkles size={16} aria-hidden="true" /> {loading ? 'Generating plan…' : 'Generate plan'}
          </button>
        </div>
      </div>
    );
  } else {
    body = (
      <div className="idea-plan">
        <div className="idea-plan-hero">
          <div>
            <h3 className="idea-plan-name">{plan.project_name}</h3>
            <p className="ai-plan-summary">{plan.summary}</p>
            <div className="idea-stats">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><ClipboardList size={14} aria-hidden="true" /> {plan.tasks.length} tasks</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Folder size={14} aria-hidden="true" /> {new Set(plan.tasks.map(t => t.phase).filter(Boolean)).size} phases</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Link2 size={14} aria-hidden="true" /> {plan.tasks.reduce((n, t) => n + (t.subtasks?.length ?? 0), 0)} subtasks</span>
            </div>
          </div>
        </div>
        <div className="idea-task-list">
          {plan.tasks.map((task, i) => (
            <div key={i} className="ai-plan-task-card">
              <div className="ai-plan-task-header">
                {task.phase && <span className="ai-phase-badge">{task.phase}</span>}
                <span className={`priority-badge priority-${task.priority ?? 'medium'}`}>{task.priority ?? 'medium'}</span>
                {task.due_date && <span className="ai-due-date" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Calendar size={12} aria-hidden="true" /> {task.due_date}</span>}
              </div>
              <div className="ai-plan-task-title">{task.title}</div>
              {task.description && <div className="ai-plan-task-desc">{task.description}</div>}
              {task.subtasks && task.subtasks.length > 0 && (
                <div className="ai-subtask-list">
                  {task.subtasks.map((sub, j) => (
                    <div key={j} className="ai-subtask-item">
                      <span className="ai-subtask-bullet"><CornerDownRight size={12} aria-hidden="true" /></span> {sub.title}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        {/* ponytail: create:true re-runs the planner server-side; send the previewed plan instead when the API accepts one. */}
        <p className="page-subtitle" style={{ padding: '0 var(--space-5)' }}>
          This is a generated preview. Creating runs the planner again with the same idea, so the tasks created can differ from it.
        </p>
        {error && <div className="ai-error" role="alert">{error}</div>}
        {createdId && <Link to={`/projects/${createdId}`} className="btn btn-secondary btn-md" onClick={onClose}>Open project</Link>}
        <div className="idea-plan-actions">
          <button type="button" className="btn btn-ghost btn-md" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}
            onClick={() => { setPlan(null); setError(''); }} disabled={creating || committed.current}>
            <ArrowLeft size={16} aria-hidden="true" /> Edit idea
          </button>
          <button type="button" className="btn btn-primary btn-md" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}
            onClick={createAll} disabled={creating || Boolean(createdId)}>
            <Rocket size={16} aria-hidden="true" /> {creating ? 'Creating project…' : 'Create project and tasks'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <Dialog open onClose={close} title="Start from an idea" closeOnBackdrop={false} className="idea-modal">
      {body}
    </Dialog>
  );
}
